/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */
import React, { useState } from "react";
import { useSelector } from "react-redux";
import { setScheduledTask } from "../../../store/api/tasks/reducer";
import { tasksSelector } from "../../../store/api/tasks/selector";
import { setErrorMsg } from "../../../store/helpers/helperReducers";
import { errorMsgSelector } from "../../../store/helpers/helperSelectors";
import { useStoreDispatch } from "../../../store/hooks";
import { constStr } from "../../../globals/constants";
import { InputField } from "../../Shared/Radix/Inputs/InputField";
import { Checkbox } from "../../ui/checkbox";

export const LteRNLocation = () => {
    const dispatch = useStoreDispatch();
    const errorMsg = useSelector(errorMsgSelector);
    const scheduledtasks = useSelector(tasksSelector);
    const [disable, setDisable] = useState({
        isRnId: false,
        isZoneId: false,
    })

    const onRadioChange = (value: string) => {
        dispatch(setScheduledTask({ lteRnLocationRequest: value }));
        dispatch(setErrorMsg({ ...errorMsg, lteRnLocationRequest: "" }));
    };

    const onInputChange = (name: string, value: string) => {
        if ((name === "radioNodeId" || name === "zoneIdValue") && value.match(/[^1-9]/)) {
            return;
        }
        dispatch(setScheduledTask({ [name]: value }));
        dispatch(setErrorMsg({ ...errorMsg, [name]: "" }));
    };

    const onCheckboxChange = (checked: boolean) => {
        dispatch(setScheduledTask({ isAll: checked, radioNodeId: "", zoneIdValue: "" }));
        setDisable({ isRnId: false, isZoneId: false });
    };

    const onBlurHandler = (e: React.FocusEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        if (name === "radioNodeId") {
            if (Number(value) > 0) {
                setDisable({ ...disable, isZoneId: true });
            } else {
                setDisable({ isRnId: false, isZoneId: false });
            }
        } else if (name === "zoneIdValue") {
            if (Number(value) > 0) {
                setDisable({ ...disable, isRnId: true });
            } else {
                setDisable({ isRnId: false, isZoneId: false });
            }
        }
    };

    const isAllSelect = scheduledtasks?.isAll === "true" ? true : scheduledtasks?.isAll ? scheduledtasks?.isAll : false;
    return (
        <div className="flex flex-col gap-6">
            <p className="text-sm font-medium">Issue a LTE Location related command</p>
            <div className="flex flex-col gap-2">
                <p className="text-sm text-muted-foreground">Request LTE RN Location</p>
                <div className="flex flex-col gap-2">
                    {[
                        { value: constStr.stopCheckLocation, label: "Stop Check Location" },
                        { value: constStr.checkLocation, label: "Check Location" },
                        { value: constStr.createBaseline, label: "Create Baseline" },
                    ].map((opt) => (
                        <label key={opt.value} className="flex items-center gap-1.5 cursor-pointer text-sm">
                            <input
                                type="radio"
                                name="lteRnLocationRequest"
                                value={opt.value}
                                checked={(scheduledtasks?.lteRnLocationRequest || constStr.checkLocation) === opt.value}
                                onChange={() => onRadioChange(opt.value)}
                                style={{ accentColor: "var(--primary)" }}
                            />
                            {opt.label}
                        </label>
                    ))}
                </div>
            </div>
            {scheduledtasks?.lteRnLocationRequest === constStr.checkLocation && (
                <>
                    <div>
                        <label className="flex items-center gap-2 text-sm cursor-pointer">
                            <Checkbox checked={isAllSelect} onCheckedChange={onCheckboxChange} />
                            All
                        </label>
                    </div>
                    <div>
                        <InputField
                            label="RN ID"
                            name="radioNodeId"
                            value={scheduledtasks?.radioNodeId ?? ""}
                            disabled={disable.isRnId || isAllSelect}
                            onChange={onInputChange}
                            onBlur={onBlurHandler}
                            placeholder="Enter rn id"
                        />
                    </div>
                    <div>
                        <InputField
                            label="Zone ID"
                            name="zoneIdValue"
                            value={scheduledtasks?.zoneIdValue ?? ""}
                            disabled={disable.isZoneId || isAllSelect}
                            placeholder="Enter zone id"
                            onChange={onInputChange}
                            onBlur={onBlurHandler}
                        />
                        <p className="text-xs mt-1" style={{ color: "var(--warning)" }}>RN ID and Zone ID are mutually exclusive.</p>
                    </div>
                </>
            )}
            {scheduledtasks?.lteRnLocationRequest === constStr.createBaseline && (
                <div>
                    <InputField
                        label="RN ID"
                        name="radioNodeId"
                        value={scheduledtasks?.radioNodeId ?? ""}
                        onChange={onInputChange}
                        placeholder="Enter rn id"
                    />
                </div>
            )}
        </div>
    );
};